import { useHistory } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { RadioGroup, Radio } from "@material-ui/core";
import { useTranslation, Trans } from "react-i18next";
import { Typography, Grid, Fade, Tooltip } from "@material-ui/core";
import { Container, Paper, Button, LinearProgress } from "@material-ui/core";
import { Accordion, AccordionSummary, AccordionDetails } from "@material-ui/core";
import { Card, CardContent, CardHeader, CircularProgress, Box, Divider } from "@material-ui/core";
import { Dialog, DialogContent, DialogTitle, DialogContentText, DialogActions } from "@material-ui/core";

// Icons
import AutorenewIcon from "@material-ui/icons/Autorenew";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";

// Components
import AlertComponent from "../../elements/Alert";
import { DotFalling } from "../../loader/Loaders";

// Connectors
import backendConnector from "../../../services/connectors/backendConnector";

// Services
import sessionStorage from "../../../services/storage/SessionStorage";

// Medias
import form_header from "../../../assets/images/form_header.png";
import icon_0 from "../../../assets/icons/top_level_commitment_and_oversight.svg";
import icon_1 from "../../../assets/icons/strategies_and_policies.png";
import icon_2 from "../../../assets/icons/risk_assessment.png";
import icon_3 from "../../../assets/icons/control_environment.svg";
import icon_4 from "../../../assets/icons/training_and_awareness.png";
import icon_5 from "../../../assets/icons/third_parties.png";
import icon_6 from "../../../assets/icons/hiring_practices_and_enforcement.png";
import icon_7 from "../../../assets/icons/gifts_entertainment_and_hospitality.svg";
import icon_8 from "../../../assets/icons/sponsorships.svg";
import icon_9 from "../../../assets/icons/charitable_contributions.svg";
import icon_10 from "../../../assets/icons/political_contributions.svg";
import icon_11 from "../../../assets/icons/facilitation_payments.svg";
import icon_12 from "../../../assets/icons/subsidiaries_and_significant_investments.svg";
import icon_13 from "../../../assets/icons/mergers_and_acquisitions.svg";
import icon_14 from "../../../assets/icons/driving_collective_impact.svg";

// Styles
import AssessmentFormStyle from "../../../assets/styles/materialUI/questionnaire/assessmentForm/AssessmentFormStyle";
import { useLanguage } from "../../../contexts/languageContext";

export default function AssessmentForm() {
	const classes = AssessmentFormStyle();
	const history = useHistory();
	const [data, setData] = useState([]);
	const [error, setError] = useState(null);
	const [answers, setAnswers] = useState({});
	const [options, setOptions] = useState({});
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState(null);
	const [sessionId, setSessionId] = useState(null);
	const [summaryOpen, setSummaryOpen] = useState(false);
	const { t } = useTranslation();
	const { direction } = useLanguage();
	const STATUS = {
		NOT_STARTED: "notStarted",
		IN_PROGRESS: "inProgress",
		COMPLETED: "completed",
	};
	const answeredQuestions = Object.keys(answers).length;

	const totalQuestions = data.reduce(
		(catAcc, cat) => catAcc + cat.subcategories.reduce((subAcc, sub) => subAcc + sub.questions.length, 0),
		0,
	);
	const progress = (answeredQuestions / totalQuestions) * 100;
	const icons = [
		icon_0,
		icon_1,
		icon_2,
		icon_3,
		icon_4,
		icon_5,
		icon_6,
		icon_7,
		icon_8,
		icon_9,
		icon_10,
		icon_11,
		icon_12,
		icon_13,
		icon_14,
	];

	const handleSubmit = () => {
		setSummaryOpen(true);
	};
	const handleSummaryClose = () => setSummaryOpen(false);

	useEffect(() => {
		let sessionId = sessionStorage.getPathName("session_id");
		if (sessionId) {
			setSessionId(sessionId);
		} else {
			history.push("/questionnaire/assessment");
		}
		constructQuestionnaire(sessionId);
	}, []);

	const handleChange = (questionId) => (event) => {
		const selectedValue = event.target.value;
		setAnswers({
			...answers,
			[questionId]: selectedValue,
		});

		let data = {
			question: questionId,
			answer: event.target.value,
			submission_id: sessionId,
		};
		let response = backendConnector.submitAnswer(data);
		if (response?.error) {
			console.log("submitAnswer error: ", response?.error);
		}
	};

	const getSubcategoryStatus = (sub, answers) => {
		const total = sub.questions.length;
		const answered = sub.questions.filter((q) => answers[q.id]).length;
		if (answered === 0) return STATUS.NOT_STARTED;
		if (answered === total) return STATUS.COMPLETED;
		return STATUS.IN_PROGRESS;
	};

	const getCategoryStatus = (cat, answers) => {
		const subStatuses = cat.subcategories.map((sub) => getSubcategoryStatus(sub, answers));
		if (subStatuses.every((s) => s === STATUS.COMPLETED)) return STATUS.COMPLETED;
		if (subStatuses.every((s) => s === STATUS.NOT_STARTED)) return STATUS.NOT_STARTED;
		return STATUS.IN_PROGRESS;
	};

	const getStatusLabel = (status) => t(`assessment.form.status.${status}`);

	const getStatusIcon = (status) => {
		switch (status) {
			case STATUS.COMPLETED:
				return <CheckCircleIcon className={classes.statusIcon} titleAccess={getStatusLabel(status)} />;
			case STATUS.IN_PROGRESS:
				return <AutorenewIcon className={classes.statusIcon} titleAccess={getStatusLabel(status)} />;
			default:
				return <RadioButtonUncheckedIcon className={classes.statusIcon} titleAccess={getStatusLabel(status)} />;
		}
	};

	const handleSummaryConfirm = async () => {
		setSummaryOpen(false);
		setLoading(true);
		let data = { submission_id: sessionId, answers: answers };
		let result = await backendConnector.submitQuestionnaire(data);
		if (result?.error) {
			setError(result?.error);
			setLoading(false);
			return;
		}
		setTimeout(() => {
			setLoading(false);
			if (progress == 100) {
				sessionStorage.clearPath("session_id");
			}
			history.push("/questionnaire/assessment_result");
		}, 1200);
	};

	const constructQuestionnaire = async (sessionId) => {
		setLoading(true);
		let response = await backendConnector.constructQuestionnaire({ submission_id: sessionId });
		if (response?.questionnaire) {
			setData(response?.questionnaire);
		} else if (response?.error) {
			setError(response?.error);
		} else {
			setError(t("assessment.landing.initError"));
		}
		if (response?.answers) {
			setAnswers(response?.answers);
		} else if (response?.error) {
			setError(response?.error);
		} else {
			setError(t("assessment.landing.initError"));
		}
		if (response?.options) {
			setOptions(response?.options);
		} else if (response?.error) {
			setError(response?.error);
		} else {
			setError(t("assessment.landing.initError"));
		}
		setLoading(false);
	};

	if (!sessionId) {
		return;
	}

	return (
		<Container component="main" dir={direction}>
			<Grid className={classes.headerContainer}>
				<Grid className={`${classes.headerSide} ${classes.headerLeft}`}>
					<Typography variant="h5" className={classes.headerText}>
						{t("assessment.form.leftHeader")}
					</Typography>
				</Grid>
				<Grid className={classes.headerImageWrap}>
					<Box component="img" src={form_header} alt="Form Header Icon" className={classes.headerImage} />
				</Grid>
				<Grid className={`${classes.headerSide} ${classes.headerRight}`}>
					<Typography variant="h5" className={classes.headerText}>
						{t("assessment.form.rightHeader")}
					</Typography>
				</Grid>
			</Grid>
			{loading && (
				<Grid className={classes.loaderContainer}>
					<DotFalling />
				</Grid>
			)}
			{!loading && (
				<Paper className={classes.paper} variant="outlined">
					<Grid>
						<Typography className={classes.assessmentHeaderText_1}>
							{t("assessment.form.headerBody")}
						</Typography>
						<Typography variant="body2" className={classes.assessmentHeaderText_2}>
							<Trans
								i18nKey="assessment.form.headerBody2"
								components={{
									0: <Typography component="span" className={classes.assessmentHeaderText_3} />,
								}}
							/>
						</Typography>
					</Grid>
					<Grid className={classes.progressParContainer}>
						<LinearProgress
							className={classes.customProgress}
							variant="determinate"
							value={Math.trunc(progress)}
						/>
						<Typography variant="subtitle2" className={classes.progressParCaption}>
							{t("assessment.form.progress", { value: Math.trunc(progress) })}
						</Typography>
					</Grid>
					<React.Fragment>
						<Grid>
							{data.map((cat, catIdx) => (
								<Accordion key={catIdx} className={classes.questionAccordion}>
									<AccordionSummary expandIcon={<ExpandMoreIcon />}>
										<Grid className={classes.questionCard}>
											<Grid className={classes.questionContainer}>
												<Typography variant="h4" className={classes.questionNumberLabel}>
													{catIdx + 1}
												</Typography>
												<Box
													component="img"
													src={icons[catIdx]}
													className={classes.questionIconBox}
												/>
												<Grid className={classes.questionCategoryContainer}>
													<Typography variant="h6" className={classes.questionCategoryText}>
														{direction === "ltr" ? cat.category : cat.category_ar}
													</Typography>
													{(cat.header || cat.header_ar) && (
														<Typography
															variant="body2"
															className={classes.questionCategoryDescription}
														>
															{direction === "ltr" ? cat.header : cat.header_ar}
														</Typography>
													)}
												</Grid>
											</Grid>
											<Typography className={classes.categoryContainerStatus}>
												{getStatusLabel(getCategoryStatus(cat, answers))}{" "}
												{getStatusIcon(getCategoryStatus(cat, answers))}
											</Typography>
										</Grid>
									</AccordionSummary>
									<AccordionDetails className={classes.accordionDetails}>
										{cat.subcategories.map((sub, subIdx) => (
											<Fade in={true} timeout={600} key={subIdx}>
												<Accordion className={classes.card}>
													<AccordionSummary expandIcon={<ExpandMoreIcon />}>
														<Typography
															variant="subtitle1"
															className={classes.questionTitleContainer}
														>
															{direction === "ltr" ? sub.title : sub.title_ar}
															<Typography className={classes.questionContainerStatus}>
																{getStatusLabel(getSubcategoryStatus(sub, answers))}{" "}
																{getStatusIcon(getSubcategoryStatus(sub, answers))}
															</Typography>
														</Typography>
													</AccordionSummary>
													<AccordionDetails className={classes.accordionDetails}>
														<Card className={classes.card}>
															<CardHeader
																title={
																	<Typography variant="subtitle2">
																		{direction === "ltr"
																			? sub.header
																			: sub.header_ar}
																	</Typography>
																}
																className={classes.cardHeader}
															/>
															<CardContent className={classes.cardContent}>
																{sub.questions.map((questionObj, qIdx) => {
																	const key = questionObj.id;
																	const selectedValue = answers[key];
																	return (
																		<Grid
																			key={key}
																			className={classes.questionBlock}
																		>
																			<Typography>
																				{qIdx + 1}.{" "}
																				{direction === "ltr"
																					? questionObj.question
																					: questionObj.question_ar}
																			</Typography>
																			<RadioGroup
																				row
																				className={classes.optionGroup}
																				value={selectedValue || ""}
																				onChange={handleChange(questionObj.id)}
																			>
																				{options[key].map((option, i) => {
																					return (
																						<Tooltip
																							title={
																								<Grid
																									className={
																										classes.tooltipContent
																									}
																									dir={direction}
																								>
																									{direction === "ltr"
																										? option?.definition
																										: option?.definition_ar}
																								</Grid>
																							}
																						>
																							<Grid
																								key={option.name}
																								className={
																									classes.questionOptionContainer
																								}
																							>
																								<Radio
																									value={option.name}
																									color="primary"
																								/>
																								<Typography
																									className={
																										classes.optionText
																									}
																								>
																									{direction === "ltr"
																										? option.description
																										: option.description_ar}
																								</Typography>
																							</Grid>
																						</Tooltip>
																					);
																				})}
																			</RadioGroup>
																			<Divider
																				className={classes.questionsDivider}
																			/>
																		</Grid>
																	);
																})}
															</CardContent>
														</Card>
													</AccordionDetails>
												</Accordion>
											</Fade>
										))}
									</AccordionDetails>
								</Accordion>
							))}
						</Grid>
					</React.Fragment>
					<Grid
						className={`w-100 d-flex justify-content-end align-items-start ${classes.submitButtonContainer}`}
					>
						<Button
							variant="contained"
							color="secondary"
							onClick={handleSubmit}
							className={classes.submitButton}
							disableElevation
							disabled={loading}
						>
							{loading ? (
								<CircularProgress size={24} className={classes.submitLoader} />
							) : (
								t("assessment.form.submit")
							)}
						</Button>
						<Dialog
							open={summaryOpen}
							onClose={handleSummaryClose}
							aria-labelledby="summary-dialog-title"
							aria-describedby="summary-dialog-description"
							dir={direction}
							fullWidth
						>
							<DialogTitle id="summary-dialog-title" className={classes.submitConfirmationDialogHeader}>
								{t("assessment.form.summary.title")}
							</DialogTitle>
							<DialogContent>
								<DialogContentText id="summary-dialog-description">
									{t("assessment.form.summary.description", {
										answered: answeredQuestions,
										total: totalQuestions,
										percent: Math.trunc(progress),
									})}
									{answeredQuestions < totalQuestions && (
										<Typography className={classes.submitConfirmationDialogBody}>
											{t("assessment.form.summary.unanswered", {
												remaining: totalQuestions - answeredQuestions,
											})}
										</Typography>
									)}
									{t("assessment.form.summary.prompt")}
								</DialogContentText>
							</DialogContent>
							<DialogActions className={classes.actionButtonContainer}>
								<Button
									onClick={handleSummaryClose}
									variant="contained"
									color="primary"
									className={classes.cancelButton}
								>
									{t("common.buttons.cancel")}
								</Button>
								<Button
									onClick={handleSummaryConfirm}
									variant="contained"
									color="primary"
									autoFocus
									className={classes.confirmButton}
								>
									{t("assessment.form.summary.confirm")}
								</Button>
							</DialogActions>
						</Dialog>
					</Grid>
				</Paper>
			)}
			{error && (
				<AlertComponent
					errors={error ? [error] : []}
					messages={message ? [message] : []}
					setError={setError}
					setMessage={setMessage}
				/>
			)}
			<Grid className={classes.footer} />
		</Container>
	);
}
